import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

export interface WaitlistEntry {
    id: string;
    tenant_id: string;
    service_id: string | null;
    phone: string;
    status: 'pending' | 'contacted' | 'booked' | 'cancelled';
    notes: string | null;
    created_at: string;
    updated_at: string;
    service?: {
        id: string;
        name: string;
    };
}

export function useWaitlist(tenantId: string | null) {
    const [entries, setEntries] = useState<WaitlistEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);

    const fetchWaitlist = async () => {
        if (!tenantId) return;

        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('waitlist')
                .select(`
          *,
          service:services(id, name)
        `)
                .eq('tenant_id', tenantId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            setEntries(data || []);
            setPendingCount((data || []).filter(e => e.status === 'pending').length);
        } catch (err) {
            console.error('Error fetching waitlist:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWaitlist();
    }, [tenantId]);

    const updateStatus = async (id: string, status: WaitlistEntry['status'], notes?: string) => {
        try {
            const { error } = await supabase
                .from('waitlist')
                .update({
                    status,
                    notes: notes || null,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setEntries(prev => prev.map(e =>
                e.id === id ? { ...e, status, notes: notes || e.notes } : e
            ));
            setPendingCount(prev => status === 'pending' ? prev : prev - 1);

            return { success: true };
        } catch (err) {
            console.error('Error updating waitlist:', err);
            return { success: false, error: err };
        }
    };

    const deleteEntry = async (id: string) => {
        try {
            const { error } = await supabase
                .from('waitlist')
                .delete()
                .eq('id', id);

            if (error) throw error;

            setEntries(prev => {
                const entry = prev.find(e => e.id === id);
                if (entry?.status === 'pending') {
                    setPendingCount(p => p - 1);
                }
                return prev.filter(e => e.id !== id);
            });

            return { success: true };
        } catch (err) {
            console.error('Error deleting waitlist entry:', err);
            return { success: false, error: err };
        }
    };

    return {
        entries,
        loading,
        pendingCount,
        updateStatus,
        deleteEntry,
        refresh: fetchWaitlist
    };
}
